import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { MapPin, Copy, Heart, ChevronDown, Calendar, ExternalLink, Church, GlassWater, Utensils, Sparkles, Cake, Car, Info, ParkingSquare } from "lucide-react";
import { Toaster, toast } from "sonner";

/* =====================================================
   CONFIGURATION — easy to update
   ===================================================== */
const WEDDING = {
  bride: "Eleonora",
  groom: "Massimiliano",
  dateISO: "2026-10-03T15:00:00+02:00",
  dateLong: "Sabato, 03 Ottobre 2026",
  city: "Lugano, Svizzera",
  // Replace with the real Google Form link when ready
  rsvpUrl: "https://forms.gle/7FYcC1p1qrr7fLRS6",
  // IBAN for the honeymoon contribution
  iban: "LT75 3250 0648 8528 8383",
  ibanHolder: "Massimiliano Sighinolfi",
  ibanBank: "Revolut Bank UAB — Konstitucijos ave. 21B, 08130, Vilnius, Lithuania",
  ibanBic: "REVOLT21",
  ceremony: {
    title: "La Cerimonia",
    name: "Basilica del Sacro Cuore",
    address: "Corso Elvezia 35A, 6900 Lugano",
    time: "Ore 15:00",
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=Basilica+del+Sacro+Cuore+Corso+Elvezia+35A+6900+Lugano",
    embed: "https://www.google.com/maps?q=Basilica+del+Sacro+Cuore+Corso+Elvezia+35A+6900+Lugano&output=embed",
    img: "/images/basilica.jpg",
  },
  reception: {
    title: "Aperitivo, Cena & Festa",
    name: "Grand Hotel Villa Castagnola",
    address: "Via Pico 9, 6900 Lugano",
    time: "Dalle ore 17:30",
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=Grand+Hotel+Villa+Castagnola+Via+Pico+9+6900+Lugano",
    embed: "https://www.google.com/maps?q=Grand+Hotel+Villa+Castagnola+Via+Pico+9+6900+Lugano&output=embed",
    img: "/images/villa-castagnola.png",
  },
  afterParty: {
    title: "Il Post Party",
    name: "CLUB.G",
    address: "Piazza Cioccaro 1, 6900 Lugano",
    time: "Dalle ore 00:00",
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=CLUB.G+Piazza+Cioccaro+1+6900+Lugano",
    embed: "https://www.google.com/maps?q=CLUB.G+Piazza+Cioccaro+1+6900+Lugano&output=embed",
    img: "/images/clubg.webp",
  },
  schedule: [
    {
      time: "15:00",
      title: "Cerimonia",
      where: "Basilica del Sacro Cuore",
      desc: "Il momento più atteso. Vi aspettiamo per condividere il nostro «sì».",
      img: "/images/basilica.jpg",
      Icon: Church,
    },
    {
      time: "17:30",
      title: "Aperitivo",
      where: "Grand Hotel Villa Castagnola",
      desc: "Brinderemo insieme all'inizio di questa nuova pagina, in un momento di gioia, sorrisi e attesa prima della cena.",
      img: "/images/aperitivo.png",
      Icon: GlassWater,
    },
    {
      time: "19:30",
      title: "Cena",
      where: "Grand Hotel Villa Castagnola",
      desc: "La serata proseguirà con una cena in un'atmosfera intima ed elegante, tra buon cibo, brindisi e momenti da custodire insieme.",
      img: "/images/villa-castagnola.png",
      Icon: Utensils,
    },
    {
      time: "22:30",
      title: "Taglio della torta",
      where: "Grand Hotel Villa Castagnola",
      desc: "Un dolce brindisi insieme per tagliare la torta e condividere il momento più dolce della serata.",
      img: "/images/torta.png",
      Icon: Cake,
    },
    {
      time: "00:00",
      title: "Party",
      where: "CLUB.G · Piazza Cioccaro 1, 6900 Lugano",
      desc: "La festa continua al CLUB.G. Per chi vorrà continuare a festeggiare con noi, dopo il ricevimento ci sposteremo al CLUB.G per il nostro post party. Per raggiungere la location sarà organizzato un transfer privato dedicato agli ospiti, con partenza direttamente dal ricevimento.",
      img: "/images/clubg.webp",
      Icon: Sparkles,
    },
  ],
};

/* =====================================================
   COUNTDOWN
   ===================================================== */
function useCountdown(targetISO) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, new Date(targetISO).getTime() - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds };
}

/* =====================================================
   COMPONENTS
   ===================================================== */
const Hero = () => (
  <section className="hero" data-testid="hero-section">
    <div className="hero-bg" aria-hidden="true" />
    <div className="hero-overlay" aria-hidden="true" />
    <div className="hero-content">
      <p className="hero-tag reveal" data-testid="hero-tag">
        Si sposano
      </p>
      <span className="hero-divider reveal delay-1" />

      <img
        src="/images/logo-me.png"
        alt="Massimiliano & Eleonora — 03 Ottobre 2026"
        className="hero-logo reveal delay-1"
        data-testid="hero-logo"
      />

      {/* Hidden but kept in DOM for SEO / a11y */}
      <h1 className="sr-only" data-testid="hero-names">
        Massimiliano &amp; Eleonora — Sabato, 03 Ottobre 2026
      </h1>
      <span className="sr-only" data-testid="hero-date">{WEDDING.dateLong}</span>

      <p
        className="hero-tag reveal delay-3"
        style={{ marginTop: "1.2rem", color: "#FAF8F5", opacity: 0.85 }}
        data-testid="hero-city"
      >
        {WEDDING.city}
      </p>

      <div className="reveal delay-4" style={{ marginTop: "3.5rem" }}>
        <a href="#countdown" data-testid="hero-scroll-cta" style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "0.6rem", color: "#E8D9BD", textDecoration: "none" }}>
          <span style={{ fontSize: "0.7rem", letterSpacing: "0.32em", textTransform: "uppercase" }}>Scopri</span>
          <ChevronDown size={20} className="animate-bounce" />
        </a>
      </div>
    </div>
  </section>
);

const Countdown = () => {
  const { days, hours, minutes, seconds } = useCountdown(WEDDING.dateISO);
  const cells = [
    { v: days, l: "Giorni" },
    { v: hours, l: "Ore" },
    { v: minutes, l: "Minuti" },
    { v: seconds, l: "Secondi" },
  ];
  return (
    <section id="countdown" className="section-pad" data-testid="countdown-section" style={{ backgroundColor: "var(--bg-primary)" }}>
      <div className="container-x" style={{ textAlign: "center" }}>
        <p className="overline">In attesa del grande giorno</p>
        <h2 className="font-heading" style={{ fontWeight: 300, fontSize: "clamp(2.4rem,5vw,3.6rem)", marginTop: "0.8rem", lineHeight: 1.1 }}>
          Mancano <em style={{ color: "var(--accent)", fontStyle: "italic" }}>solo</em>
        </h2>

        <div className="countdown-grid" style={{ marginTop: "3rem" }} data-testid="countdown-grid">
          {cells.map((c, i) => (
            <div key={i} className="countdown-cell" data-testid={`countdown-${c.l.toLowerCase()}`}>
              <div className="countdown-num">{String(c.v).padStart(2, "0")}</div>
              <div className="countdown-label">{c.l}</div>
            </div>
          ))}
        </div>

        <div className="ornament-divider" style={{ marginTop: "3rem" }}>
          <span className="line" /><span className="dot" /><span className="line" />
        </div>
      </div>
    </section>
  );
};

const Story = () => (
  <section className="section-pad" data-testid="story-section" style={{ backgroundColor: "var(--bg-secondary)" }}>
    <div className="container-x">
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "3rem", alignItems: "center" }} className="md:grid-cols-2">
        <div className="reveal">
          <p className="overline">Il nostro racconto</p>
          <h2 className="font-heading" style={{ fontWeight: 300, fontSize: "clamp(2.4rem,5vw,4rem)", lineHeight: 1.1, marginTop: "1rem" }}>
            Le nostre anime hanno scelto<br />
            <em style={{ color: "var(--accent)" }}>di camminare insieme, per sempre.</em>
          </h2>
          <p style={{ marginTop: "2rem", fontSize: "1.05rem", lineHeight: 1.85, color: "var(--text-secondary)", maxWidth: "32rem" }}>
            Ci siamo incontrati per caso, ci siamo scelti per amore.
            <br /><br />
            Da quel momento, ogni giorno è diventato parte della nostra
            storia: un intreccio di piccoli gesti, sogni condivisi e cammini
            percorsi insieme.
            <br /><br />
            Il <strong style={{ color: "var(--text-primary)", fontWeight: 500 }}>3 ottobre 2026</strong> ne
            vivremo il capitolo più dolce, circondati da chi ha accompagnato,
            con affetto, il nostro cammino.
          </p>
          <p className="font-heading" style={{ fontStyle: "italic", marginTop: "2rem", fontSize: "1.4rem", color: "var(--text-primary)" }}>
            «Con tutto il cuore, vi aspettiamo a Lugano.»
          </p>
        </div>

        <div className="reveal delay-2" style={{ position: "relative" }}>
          <div className="arch-img" style={{ aspectRatio: "3/4", maxWidth: "440px", margin: "0 auto" }}>
            <img src="/images/couple.png" alt="Massimiliano & Eleonora" />
          </div>
          <div className="font-script" style={{ position: "absolute", top: "-1.5rem", right: "0.5rem", fontSize: "2.4rem", color: "var(--accent)", transform: "rotate(-6deg)" }}>
            M &amp; E
          </div>
        </div>
      </div>

      <style>{`
        @media (min-width: 768px) {
          [data-testid="story-section"] .container-x > div {
            grid-template-columns: 1fr 1fr;
            gap: 6rem;
          }
        }
      `}</style>
    </div>
  </section>
);

const Schedule = () => (
  <section id="programma" className="section-pad" data-testid="schedule-section" style={{ backgroundColor: "var(--bg-primary)" }}>
    <div className="container-x">
      <div style={{ textAlign: "center", marginBottom: "5rem" }}>
        <p className="overline">Il programma della giornata</p>
        <h2 className="font-heading" style={{ fontWeight: 300, fontSize: "clamp(2.4rem,5vw,4rem)", lineHeight: 1.1, marginTop: "1rem" }}>
          Un giorno<br />
          <em style={{ color: "var(--accent)" }}>da ricordare.</em>
        </h2>
        <span className="thin-divider" style={{ marginTop: "2rem" }} />
      </div>

      <div className="timeline-rail" style={{ maxWidth: "62rem", margin: "0 auto" }}>
        {WEDDING.schedule.map((s, idx) => {
          const Icon = s.Icon;
          const side = idx % 2 === 0 ? "left" : "right";
          return (
            <div key={idx} className={`timeline-item ${side}`} data-testid={`timeline-item-${idx}`}>
              <div className="timeline-dot" />
              <div className="timeline-content">
                <div className="timeline-time">{s.time}</div>
                <h3 className="timeline-title">
                  <Icon size={18} style={{ display: "inline", marginRight: "0.5rem", color: "var(--accent)" }} />
                  {s.title}
                </h3>
                <p className="timeline-sub"><strong style={{ color: "var(--text-primary)", fontWeight: 500 }}>{s.where}</strong></p>
                <p className="timeline-sub" style={{ marginTop: "0.4rem" }}>{s.desc}</p>
              </div>
              <div className="timeline-image">
                <div className="timeline-thumb">
                  <img src={s.img} alt={s.title} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const LocationCard = ({ data, testid, reverse }) => (
  <div className="location-card reveal" data-testid={testid}>
    <div className="location-img">
      <img src={data.img} alt={data.name} />
    </div>
    <div className="location-body">
      <p className="overline">{data.title}</p>
      <h3 className="font-heading" style={{ fontWeight: 400, fontSize: "2rem", marginTop: "0.6rem", lineHeight: 1.2 }}>
        {data.name}
      </h3>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem", marginTop: "1.2rem", color: "var(--text-secondary)" }}>
        <MapPin size={18} style={{ color: "var(--accent)", marginTop: "2px", flexShrink: 0 }} />
        <span style={{ fontSize: "0.95rem", lineHeight: 1.6 }}>{data.address}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginTop: "0.6rem", color: "var(--text-secondary)" }}>
        <Calendar size={18} style={{ color: "var(--accent)" }} />
        <span style={{ fontSize: "0.95rem" }}>{data.time}</span>
      </div>

      <div style={{ marginTop: "1.8rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        <a
          href={data.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-ghost"
          data-testid={`${testid}-maps-link`}
        >
          Indicazioni stradali <ExternalLink size={14} />
        </a>
      </div>

      <div style={{ marginTop: "1.8rem", border: "1px solid var(--border-warm)", overflow: "hidden", height: "260px" }}>
        <iframe
          title={`Mappa ${data.name}`}
          src={data.embed}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          data-testid={`${testid}-embed`}
        />
      </div>
    </div>
  </div>
);

const Locations = () => (
  <section id="luoghi" className="section-pad" data-testid="locations-section" style={{ backgroundColor: "var(--bg-secondary)" }}>
    <div className="container-x">
      <div style={{ textAlign: "center", marginBottom: "4.5rem" }}>
        <p className="overline">I luoghi della festa</p>
        <h2 className="font-heading" style={{ fontWeight: 300, fontSize: "clamp(2.4rem,5vw,4rem)", lineHeight: 1.1, marginTop: "1rem" }}>
          Lugano,<br />
          <em style={{ color: "var(--accent)" }}>il nostro «sì» sul lago.</em>
        </h2>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "2.5rem" }} className="locations-grid">
        <LocationCard data={WEDDING.ceremony} testid="ceremony-card" />
        <LocationCard data={WEDDING.reception} testid="reception-card" reverse />
        <LocationCard data={WEDDING.afterParty} testid="afterparty-card" />
      </div>

      <style>{`
        @media (min-width: 900px) {
          .locations-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 3rem !important; }
        }
        @media (min-width: 1280px) {
          .locations-grid { grid-template-columns: repeat(3, 1fr) !important; gap: 2.5rem !important; }
        }
      `}</style>
    </div>
  </section>
);

const AddressLink = ({ address, children, testid }) => (
  <a
    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    style={{
      color: "var(--text-secondary)",
      fontSize: "0.95rem",
      lineHeight: 1.7,
      display: "inline-flex",
      alignItems: "center",
      gap: "0.4rem",
      textDecoration: "none",
      borderBottom: "1px dotted var(--border-warm)",
      paddingBottom: "1px",
      transition: "color 200ms ease, border-color 200ms ease",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.color = "var(--accent-hover)";
      e.currentTarget.style.borderBottomColor = "var(--accent)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.color = "var(--text-secondary)";
      e.currentTarget.style.borderBottomColor = "var(--border-warm)";
    }}
  >
    <MapPin size={14} style={{ color: "var(--accent)", flexShrink: 0 }} />
    <span>{children || address}</span>
    <ExternalLink size={11} style={{ color: "var(--accent)", opacity: 0.7, flexShrink: 0 }} />
  </a>
);

const Parking = () => (
  <section id="parcheggi" className="section-pad" data-testid="parking-section" style={{ backgroundColor: "var(--bg-primary)" }}>
    <div className="container-x" style={{ maxWidth: "68rem" }}>
      <div style={{ textAlign: "center", marginBottom: "4rem" }}>
        <p className="overline">Info pratiche</p>
        <h2 className="font-heading" style={{ fontWeight: 300, fontSize: "clamp(2.4rem,5vw,4rem)", lineHeight: 1.1, marginTop: "1rem" }}>
          Parcheggi &amp;<br />
          <em style={{ color: "var(--accent)" }}>come arrivare.</em>
        </h2>
        <span className="thin-divider" style={{ marginTop: "2rem" }} />
      </div>

      <div style={{ display: "grid", gap: "2rem" }}>
        {/* VIGNETTA — spostata PRIMA */}
        <div className="card-warm" data-testid="parking-vignetta" style={{ background: "var(--bg-secondary)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.8rem", marginBottom: "1rem" }}>
            <Info size={22} style={{ color: "var(--accent)" }} />
            <p className="overline" style={{ margin: 0 }}>Se venite in auto · importante</p>
          </div>
          <h3 className="font-heading" style={{ fontWeight: 400, fontSize: "1.6rem", color: "var(--text-primary)", marginBottom: "1.2rem" }}>
            Vignetta autostradale svizzera
          </h3>
          <p style={{ color: "var(--text-secondary)", lineHeight: 1.8 }}>
            Ricordati che, per utilizzare <strong style={{ color: "var(--text-primary)" }}>autostrade e
            semiautostrade svizzere</strong>, è obbligatoria la vignetta autostradale svizzera.
            La vignetta ha validità per l'anno solare e può essere acquistata:
          </p>
          <ul style={{ color: "var(--text-secondary)", lineHeight: 1.9, marginTop: "0.8rem", paddingLeft: "1.4rem" }}>
            <li>in formato fisico, anche presso la dogana;</li>
            <li>
              in formato elettronico sul sito ufficiale:{" "}
              <a
                href="https://via.admin.ch/shop/config/evignette"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="vignetta-link"
                style={{ color: "var(--accent)", textDecoration: "underline" }}
              >
                via.admin.ch/shop/config/evignette
              </a>
            </li>
          </ul>
          <p style={{ color: "var(--text-secondary)", lineHeight: 1.8, marginTop: "1rem", fontStyle: "italic" }}>
            Se invece raggiungerai Lugano evitando autostrade e semiautostrade svizzere,
            la vignetta non è necessaria.
          </p>
        </div>

        {/* CERIMONIA */}
        <div className="card-warm" data-testid="parking-cerimonia">
          <div style={{ display: "flex", alignItems: "center", gap: "0.8rem", marginBottom: "1rem" }}>
            <Car size={22} style={{ color: "var(--accent)" }} />
            <p className="overline" style={{ margin: 0 }}>Parcheggi per la Cerimonia</p>
          </div>
          <h3 className="font-heading" style={{ fontWeight: 400, fontSize: "1.6rem", color: "var(--text-primary)", marginBottom: "1.2rem" }}>
            Basilica del Sacro Cuore
          </h3>
          <p style={{ color: "var(--text-secondary)", lineHeight: 1.8, marginBottom: "1.2rem" }}>
            Per chi arriva in auto, consigliamo:
          </p>

          <div style={{ paddingLeft: "1rem", borderLeft: "2px solid var(--border-warm)", marginBottom: "1.4rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--text-primary)", fontWeight: 500, marginBottom: "0.4rem" }}>
              <ParkingSquare size={16} style={{ color: "var(--accent)" }} /> Autosilo Balestra
            </div>
            <AddressLink address="Autosilo Balestra, Via G.B. Pioda 9, 6900 Lugano" testid="parking-balestra-map">
              Via G.B. Pioda 9, 6900 Lugano
            </AddressLink>
            <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem", marginTop: "0.5rem", fontStyle: "italic" }}>
              Autosilo coperto, aperto 24/24.
            </div>
          </div>

          <div style={{ paddingLeft: "1rem", borderLeft: "2px solid var(--border-warm)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--text-primary)", fontWeight: 500, marginBottom: "0.4rem" }}>
              <ParkingSquare size={16} style={{ color: "var(--accent)" }} /> Posteggi nelle vie circostanti la Basilica
            </div>
            <div style={{ color: "var(--text-secondary)", fontSize: "0.95rem", lineHeight: 1.7 }}>
              Nella zona sono presenti diversi stalli pubblici con strisce blu e bianche.
              Ti consigliamo di controllare sempre la segnaletica e l'eventuale parchimetro,
              perché modalità di pagamento e durata massima della sosta possono variare da
              una zona all'altra.
            </div>
          </div>
        </div>

        {/* VILLA CASTAGNOLA */}
        <div className="card-warm" data-testid="parking-villa">
          <div style={{ display: "flex", alignItems: "center", gap: "0.8rem", marginBottom: "1rem" }}>
            <GlassWater size={22} style={{ color: "var(--accent)" }} />
            <p className="overline" style={{ margin: 0 }}>Aperitivo &amp; Cena</p>
          </div>
          <h3 className="font-heading" style={{ fontWeight: 400, fontSize: "1.6rem", color: "var(--text-primary)", marginBottom: "0.5rem" }}>
            Grand Hotel Villa Castagnola
          </h3>
          <div style={{ marginBottom: "1.4rem" }}>
            <AddressLink address="Grand Hotel Villa Castagnola, Via Pico 9, 6900 Lugano" testid="parking-villa-main-map">
              Via Pico 9, 6906 Lugano
            </AddressLink>
          </div>
          <p style={{ color: "var(--text-secondary)", lineHeight: 1.8, marginBottom: "1.4rem" }}>
            La Villa dispone di parcheggi interni a pagamento, disponibili fino ad esaurimento posti.
            Il nostro consiglio è quello di valutare le diverse alternative nelle vicinanze:
          </p>

          <div style={{ display: "grid", gap: "1.2rem" }}>
            <div style={{ paddingLeft: "1rem", borderLeft: "2px solid var(--border-warm)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--text-primary)", fontWeight: 500, marginBottom: "0.3rem" }}>
                <ParkingSquare size={16} style={{ color: "var(--accent)" }} /> Area parcheggi Lido / Piscina Coperta
              </div>
              <AddressLink address="Lido Lugano, Viale Castagnola 4, 6906 Lugano" testid="parking-lido-map">
                Viale Castagnola 4, 6906 Lugano
              </AddressLink>
            </div>

            <div style={{ paddingLeft: "1rem", borderLeft: "2px solid var(--border-warm)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--text-primary)", fontWeight: 500, marginBottom: "0.3rem" }}>
                <ParkingSquare size={16} style={{ color: "var(--accent)" }} /> Parcheggio Campo Marzio
              </div>
              <AddressLink address="Parcheggio Campo Marzio, Via Pietro Capelli, 6900 Lugano" testid="parking-campomarzio-map">
                Via Pietro Capelli, 6900 Lugano
              </AddressLink>
              <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem", marginTop: "0.4rem", fontStyle: "italic" }}>
                Ampio parcheggio pubblico con circa 160 posti.
              </div>
            </div>

            <div style={{ paddingLeft: "1rem", borderLeft: "2px solid var(--border-warm)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--text-primary)", fontWeight: 500, marginBottom: "0.3rem" }}>
                <ParkingSquare size={16} style={{ color: "var(--accent)" }} /> Posteggi pubblici lungo Viale Castagnola
              </div>
              <AddressLink address="Viale Castagnola 16, 6906 Lugano" testid="parking-vialecastagnola-map">
                zona Viale Castagnola 16
              </AddressLink>
            </div>

            <div style={{ paddingLeft: "1rem", borderLeft: "2px solid var(--border-warm)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--text-primary)", fontWeight: 500, marginBottom: "0.3rem" }}>
                <ParkingSquare size={16} style={{ color: "var(--accent)" }} /> Parcheggio Piazza della Riscossa
              </div>
              <AddressLink address="Piazza della Riscossa, 6906 Lugano" testid="parking-riscossa-map">
                Piazza della Riscossa, 6906 Lugano
              </AddressLink>
            </div>
          </div>

          <p style={{ color: "var(--text-secondary)", lineHeight: 1.8, marginTop: "1.6rem", fontStyle: "italic", fontSize: "0.95rem" }}>
            Nelle vie limitrofe a Villa Castagnola sono inoltre presenti posteggi pubblici blu
            e bianchi: alcuni possono essere gratuiti o avere fasce/orari differenti, quindi ti
            consigliamo sempre di verificare la segnaletica presente sul posto e l'eventuale
            parchimetro.
          </p>
        </div>
      </div>
    </div>
  </section>
);


const Gift = () => {
  const copyIban = async () => {
    try {
      await navigator.clipboard.writeText(WEDDING.iban.replace(/\s+/g, ""));
      toast.success("IBAN copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare l'IBAN");
    }
  };

  return (
    <section id="regalo" className="section-pad" data-testid="gift-section" style={{ backgroundColor: "var(--bg-primary)" }}>
      <div className="container-x" style={{ maxWidth: "56rem", textAlign: "center" }}>
        <p className="overline">Lista nozze</p>
        <h2 className="font-heading" style={{ fontWeight: 300, fontSize: "clamp(2.4rem,5vw,4rem)", lineHeight: 1.1, marginTop: "1rem" }}>
          Il regalo<br />
          <em style={{ color: "var(--accent)" }}>più bello.</em>
        </h2>
        <span className="thin-divider" style={{ marginTop: "2rem" }} />

        <p style={{ marginTop: "2.5rem", fontSize: "1.1rem", lineHeight: 1.9, color: "var(--text-secondary)" }}>
          La vostra presenza sarà già il regalo più prezioso.
          <br /><br />
          Per chi desiderasse farci un pensiero e fosse indeciso su cosa
          scegliere, abbiamo pensato a un contributo al nostro
          <strong style={{ color: "var(--text-primary)", fontWeight: 500 }}> viaggio di nozze</strong>:
          un piccolo gesto che diventerà parte delle prossime avventure che
          vivremo insieme.
        </p>
        <p className="font-heading" style={{ fontStyle: "italic", marginTop: "1.5rem", fontSize: "1.3rem", color: "var(--text-primary)" }}>
          «Grazie per essere parte del nostro viaggio.»
        </p>

        <div className="card-warm" style={{ marginTop: "3rem", textAlign: "left" }}>
          <p className="overline" style={{ textAlign: "center" }}>Coordinate per il bonifico</p>

          <div style={{ marginTop: "2rem", display: "grid", gap: "1.4rem" }}>
            <div>
              <div style={{ fontSize: "0.7rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--accent)", marginBottom: "0.4rem" }}>Intestatari</div>
              <div className="font-heading" style={{ fontSize: "1.4rem", fontWeight: 400 }} data-testid="iban-holder">{WEDDING.ibanHolder}</div>
            </div>

            <div>
              <div style={{ fontSize: "0.7rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--accent)", marginBottom: "0.4rem" }}>Banca</div>
              <div style={{ fontSize: "1rem", color: "var(--text-primary)" }} data-testid="iban-bank">{WEDDING.ibanBank}</div>
            </div>

            <div>
              <div style={{ fontSize: "0.7rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--accent)", marginBottom: "0.6rem" }}>IBAN</div>
              <div className="iban-box">
                <span className="iban-code" data-testid="iban-code">{WEDDING.iban}</span>
                <button onClick={copyIban} className="btn-ghost" data-testid="iban-copy-button" style={{ borderBottom: "1px solid var(--accent)" }}>
                  <Copy size={14} /> Copia
                </button>
              </div>
            </div>

            <div>
              <div style={{ fontSize: "0.7rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--accent)", marginBottom: "0.4rem" }}>Codice BIC / SWIFT</div>
              <div className="iban-code" style={{ fontSize: "1rem", color: "var(--text-primary)" }} data-testid="iban-bic">{WEDDING.ibanBic}</div>
            </div>

            <p style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginTop: "0.5rem", textAlign: "center", fontStyle: "italic" }}>
              Causale suggerita: «Viaggio di nozze Massimiliano &amp; Eleonora»
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const RSVP = () => (
  <section id="rsvp" className="rsvp-banner section-pad" data-testid="rsvp-section">
    <div className="container-x" style={{ maxWidth: "44rem" }}>
      <p className="overline" data-testid="rsvp-overline">Conferma la tua presenza</p>
      <h2 className="font-heading" style={{ fontWeight: 300, fontSize: "clamp(2.4rem,5.5vw,4.4rem)", lineHeight: 1.1, marginTop: "1rem", color: "#fff" }}>
        Ci sarai con noi?<br />
        <em style={{ color: "#E8D9BD" }}>Faccelo sapere.</em>
      </h2>
      <p style={{ marginTop: "1.6rem", color: "#E8D9BD", fontSize: "1.05rem", lineHeight: 1.8, opacity: 0.92 }}>
        Per aiutarci con l'organizzazione, ti chiediamo gentilmente di confermare
        la tua presenza <strong style={{ color: "#fff" }}>il prima possibile</strong> compilando
        il modulo qui sotto.
        <br /><br />
        <span style={{ color: "#fff" }}>Importante:</span> nel modulo di conferma
        vi chiediamo di segnalarci anche se desiderate fermarvi con noi al
        party. La location sarà svelata più avanti e sarà sempre nelle vicinanze.
      </p>

      <div style={{ marginTop: "2.5rem", display: "flex", justifyContent: "center", gap: "1rem", flexWrap: "wrap" }}>
        <a
          href={WEDDING.rsvpUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary"
          data-testid="rsvp-button"
          style={{ background: "#FAF8F5", color: "var(--bg-dark)", borderColor: "#FAF8F5" }}
        >
          <Heart size={14} /> Conferma qui
        </a>
      </div>

      <p style={{ marginTop: "2rem", fontSize: "0.78rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#E8D9BD", opacity: 0.7 }}>
        {WEDDING.dateLong} · {WEDDING.city}
      </p>
    </div>
  </section>
);

const Footer = () => (
  <footer className="footer" data-testid="footer">
    <div className="ornament-divider" style={{ marginBottom: "1.8rem" }}>
      <span className="line" /><span className="dot" /><span className="line" />
    </div>
    <p className="font-script" style={{ fontSize: "2.2rem", color: "var(--accent)" }}>
      Ci vediamo a Lugano
    </p>
    <p className="font-heading" style={{ marginTop: "1rem", fontSize: "1.6rem", fontWeight: 400, color: "var(--text-primary)" }}>
      {WEDDING.groom} <span style={{ color: "var(--accent)", fontStyle: "italic" }}>&amp;</span> {WEDDING.bride}
    </p>
    <p style={{ marginTop: "0.6rem", fontSize: "0.78rem", letterSpacing: "0.3em", textTransform: "uppercase", color: "var(--text-secondary)" }}>
      03 · 10 · 2026
    </p>
    <p style={{ marginTop: "2.5rem", fontSize: "0.75rem", color: "var(--text-secondary)" }}>
      Realizzato con amore, per chi vorrà esserci.
    </p>
  </footer>
);

const NavBar = () => (
  <nav
    data-testid="navbar"
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 50,
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      background: "rgba(250, 248, 245, 0.78)",
      borderBottom: "1px solid var(--border-warm)",
    }}
  >
    <div className="container-x" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 1.5rem" }}>
      <a href="#" className="nav-logo-link" data-testid="nav-logo" style={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}>
        <img
          src="/images/logo-mark.png"
          alt="Massimiliano & Eleonora"
          className="nav-logo-img"
        />
      </a>
      <div style={{ display: "flex", gap: "1.8rem", alignItems: "center" }} className="nav-links">
        <a href="#countdown" className="hidden md:inline" style={{ fontSize: "0.72rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--text-secondary)", textDecoration: "none" }}>Countdown</a>
        <a href="#programma" className="hidden md:inline" style={{ fontSize: "0.72rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--text-secondary)", textDecoration: "none" }}>Programma</a>
        <a href="#luoghi" className="hidden md:inline" style={{ fontSize: "0.72rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--text-secondary)", textDecoration: "none" }}>Luoghi</a>
        <a href="#parcheggi" className="hidden md:inline" style={{ fontSize: "0.72rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--text-secondary)", textDecoration: "none" }}>Parcheggi</a>
        <a href="#regalo" className="hidden md:inline" style={{ fontSize: "0.72rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--text-secondary)", textDecoration: "none" }}>Regalo</a>
        <a
          href={WEDDING.rsvpUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary"
          data-testid="nav-rsvp-button"
          style={{ padding: "0.6rem 1.2rem", fontSize: "0.7rem" }}
        >
          Conferma qui
        </a>
      </div>
    </div>
    <style>{`
      @media (max-width: 767px) {
        .nav-links a:not(.btn-primary) { display: none !important; }
      }
    `}</style>
  </nav>
);

const Home = () => (
  <div>
    <NavBar />
    <Hero />
    <Countdown />
    <Story />
    <Schedule />
    <Locations />
    <Parking />
    <Gift />
    <RSVP />
    <Footer />
    <Toaster position="bottom-center" theme="light" toastOptions={{ style: { background: "#FAF8F5", color: "#2A2927", border: "1px solid #E3DCCF" } }} />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
